import {
    ArrowForward,
    CheckCircle,
    Email,
    KeyboardArrowDown,
    LocationOn,
    People,
    Phone,
    PlayArrow,
    Schedule,
    Star,
    Verified
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Fade,
    Grid,
    IconButton,
    Rating,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
    Zoom,
    useMediaQuery,
    useTheme
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getOrDefault, getRightOrLeft, isRTL } from '../../../../Helpers/Utils';
import { ServiceHeroProps, StatItem } from './types';

const ServiceHero: React.FC<ServiceHeroProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isExtraSmall = useMediaQuery('(max-width:480px)');

    // Animation states
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [imageLoaded, setImageLoaded] = useState<boolean>(false);

    // Extract spec properties with defaults using theme colors
    const backgroundColor = getOrDefault(
        spec.backgroundColor,
        theme.palette.background.default
    );
    const textColor = getOrDefault(
        spec.textColor,
        theme.palette.text.primary
    );
    const accentColor = getOrDefault(spec.accentColor, theme.palette.primary.main);
    const title = getOrDefault(spec.title, 'احترافية في الخدمة');
    const subtitle = getOrDefault(spec.subtitle, 'نقدم لك أفضل الخدمات المتخصصة');
    const description = getOrDefault(
        spec.description,
        'فريقنا من الخبراء مستعد لتقديم خدمات عالية الجودة تلبي احتياجاتك وتفوق توقعاتك'
    );
    const heroImage = getOrDefault(spec.heroImage, null);
    const heroImages = getOrDefault(spec.heroImages, []);
    const ctaText = getOrDefault(spec.ctaText, 'احجز استشارة مجانية');
    const ctaLink = getOrDefault(spec.ctaLink, '#');
    const secondaryCtaText = getOrDefault(spec.secondaryCtaText, 'تعرف على خدماتنا');
    const secondaryCtaLink = getOrDefault(spec.secondaryCtaLink, '#');
    const showVideoButton = getOrDefault(spec.showVideoButton, false);
    const videoUrl = getOrDefault(spec.videoUrl, '#');
    const showStats = getOrDefault(spec.showStats, true);
    const stats: StatItem[] = getOrDefault(spec.stats, [
        { label: 'عميل راضي', value: '500+', icon: <People /> },
        { label: 'سنة خبرة', value: '10+', icon: <Schedule /> },
        { label: 'مشروع منجز', value: '1000+', icon: <CheckCircle /> },
        { label: 'تقييم العملاء', value: '4.9', icon: <Star /> }
    ]);
    const features = getOrDefault(spec.features, []);
    const testimonial = getOrDefault(spec.testimonial, null);
    const contactInfo = getOrDefault(spec.contactInfo, null);
    const showScrollIndicator = getOrDefault(spec.showScrollIndicator, true);
    const height = getOrDefault(spec.height, '100vh');
    const overlay = getOrDefault(
        spec.overlay,
        'rgba(0,0,0,0.3)'
    );
    const showBadge = getOrDefault(spec.showBadge, false);
    const badgeText = getOrDefault(spec.badgeText, 'خدمة معتمدة');
    const animationSpeed = getOrDefault(spec.animationSpeed, 5000);
    const enableParallax = getOrDefault(spec.enableParallax, false);

    // Animation effect
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
            setIsLoading(false);
        }, 100);

        // Image carousel effect
        if (heroImages && heroImages.length > 1) {
            const interval = setInterval(() => {
                setCurrentImageIndex(prev => (prev + 1) % heroImages.length);
                setImageLoaded(false);
            }, animationSpeed);
            return () => {
                clearInterval(interval);
                clearTimeout(timer);
            };
        }

        return () => clearTimeout(timer);
    }, [heroImages, animationSpeed]);

    // Get current background image
    const getCurrentImage = useCallback(() => {
        if (heroImages && heroImages.length > 0) {
            return heroImages[currentImageIndex];
        }
        return heroImage;
    }, [heroImages, currentImageIndex, heroImage]);

    // Preload images
    useEffect(() => {
        const currentImage = getCurrentImage();
        if (currentImage) {
            const img = new Image();
            img.src = currentImage;
            img.onload = () => setImageLoaded(true);
        } else {
            setImageLoaded(true);
        }
    }, [currentImageIndex, getCurrentImage]);



    // Handle scroll for parallax effect
    useEffect(() => {
        if (!enableParallax || isMobile) return undefined;

        const handleScroll = () => {
            const scrolled = window.pageYOffset;
            const parallaxElement = document.getElementById(`service-hero-${index}`);
            if (parallaxElement) {
                parallaxElement.style.transform = `translateY(${scrolled * 0.5}px)`;
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [enableParallax, isMobile, index]);

    // Background styles with memoization
    const backgroundStyles = useMemo(() => ({
        minHeight: isMobile ? (isExtraSmall ? '80vh' : '85vh') : height,
        background: getCurrentImage()
            ? `linear-gradient(${overlay}, ${overlay}), url(${getCurrentImage()}) center/cover no-repeat`
            : backgroundColor,
        backgroundAttachment: isSmallMobile ? 'scroll' : 'fixed',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        py: { xs: 2, sm: 3, md: 0 },
        transition: 'background 0.5s ease-in-out'
    }), [isMobile, isExtraSmall, isSmallMobile, height, overlay, backgroundColor, getCurrentImage]);

    // Animation keyframes with RTL support
    const floatingAnimation = `
        @keyframes floating {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
        }
        @keyframes slideInFromLeft {
            from { opacity: 0; transform: translateX(${isRTL() ? '50px' : '-50px'}); }
            to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInFromRight {
            from { opacity: 0; transform: translateX(${isRTL() ? '-50px' : '50px'}); }
            to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes ripple {
            0% {
                transform: translate(-50%, -50%) scale(1);
                opacity: 1;
            }
            100% {
                transform: translate(-50%, -50%) scale(1.5);
                opacity: 0;
            }
        }
        @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
        }
    `;

    return (
        <Box
            key={`${type}-${index}-box`}
            id={spec.id}
            sx={backgroundStyles}
        >
            {/* Add floating animations */}
            <style>{floatingAnimation}</style>

            {/* Loading skeleton */}
            {isLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: theme.palette.background.default,
                        zIndex: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Container maxWidth="xl">
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Skeleton variant="text" width="60%" height={80} />
                                <Skeleton variant="text" width="80%" height={60} />
                                <Skeleton variant="text" width="90%" height={40} />
                                <Skeleton variant="rectangular" width="70%" height={56} sx={{ mt: 3, borderRadius: 3 }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                    {[1, 2, 3, 4].map((i: number) => (
                                        <Grid item xs={6} key={i}>
                                            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            )}

            {/* Animated background particles */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(circle at 20% 20%, ${accentColor}20 0%, transparent 50%), 
                                radial-gradient(circle at 80% 80%, ${accentColor}15 0%, transparent 50%)`,
                    opacity: imageLoaded ? 0.6 : 0,
                    animation: 'floating 6s ease-in-out infinite',
                    transition: 'opacity 0.5s ease-in-out'
                }}
            />

            <Container maxWidth="xl" sx={{
                position: 'relative',
                zIndex: 2,
                px: { xs: 2, sm: 3, md: 4 }
            }}>
                <Grid
                    container
                    spacing={{ xs: 2, sm: 3, md: 4 }}
                    alignItems="center"
                    minHeight="inherit"
                    sx={{
                        py: { xs: 4, sm: 6, md: 8 }
                    }}
                >
                    {/* Left Content */}
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            mb: { xs: 4, md: 0 },
                            textAlign: { xs: 'center', md: getRightOrLeft() }
                        }}
                    >
                        <Box sx={{
                            animation: isVisible ? 'slideInFromLeft 1s ease-out' : 'none',
                        }}>
                            {/* Badge/Tag */}
                            {showBadge && (
                                <Fade in={isVisible} timeout={800}>
                                    <Tooltip
                                        title={isRTL() ? "معتمد من قبل الخبراء" : "Certified by Experts"}
                                        arrow
                                        placement="top"
                                    >
                                        <Chip
                                            icon={<Verified />}
                                            label={badgeText}
                                            sx={{
                                                mb: 3,
                                                background: `linear-gradient(${isRTL() ? '315deg' : '45deg'}, ${accentColor}, ${accentColor}80)`,
                                                color: theme.palette.getContrastText(accentColor),
                                                fontWeight: 600,
                                                fontSize: '0.9rem',
                                                px: 1,
                                                boxShadow: `0 4px 20px ${accentColor}40`,
                                                transition: 'all 0.3s ease',
                                                cursor: 'default',
                                                '& .MuiChip-icon': {
                                                    marginRight: isRTL() ? '-6px' : 'auto',
                                                    marginLeft: isRTL() ? 'auto' : '-6px'
                                                },
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                    boxShadow: `0 6px 25px ${accentColor}60`
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                </Fade>
                            )}

                            {/* Title */}
                            <Fade in={isVisible} timeout={1000}>
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem', lg: '4rem' },
                                        fontWeight: 800,
                                        color: textColor,
                                        mb: { xs: 2, md: 2 },
                                        lineHeight: { xs: 1.2, md: 1.2 },
                                        textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                        textAlign: { xs: 'center', md: getRightOrLeft() }
                                    }}
                                >
                                    {title}
                                </Typography>
                            </Fade>

                            {/* Subtitle */}
                            <Fade in={isVisible} timeout={1200}>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
                                        fontWeight: 400,
                                        //get color based on theme
                                        color: `${textColor}ff`,
                                        mb: { xs: 2, md: 3 },
                                        lineHeight: 1.5,
                                        textAlign: { xs: 'center', md: getRightOrLeft() }
                                    }}
                                >
                                    {subtitle}
                                </Typography>
                            </Fade>

                            {/* Description */}
                            <Fade in={isVisible} timeout={1400}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                        color: `${textColor}ff`,
                                        mb: { xs: 3, md: 4 },
                                        lineHeight: { xs: 1.6, md: 1.8 },
                                        maxWidth: { xs: '100%', md: '500px' },
                                        textAlign: { xs: 'center', md: getRightOrLeft() }
                                    }}
                                >
                                    {description}
                                </Typography>
                            </Fade>

                            {/* Features List */}
                            {features && features.length > 0 && (
                                <Fade in={isVisible} timeout={1600}>
                                    <Stack
                                        spacing={1}
                                        sx={{
                                            mb: { xs: 3, md: 4 },
                                            alignItems: { xs: 'center', md: isRTL() ? 'flex-end' : 'flex-start' }
                                        }}
                                    >
                                        {features.slice(0, 3).map((feature: string, idx: number) => (
                                            <Box key={idx} sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                justifyContent: { xs: 'center', md: isRTL() ? 'flex-end' : 'flex-start' },
                                                flexDirection: isRTL() ? 'row-reverse' : 'row'
                                            }}>
                                                <CheckCircle sx={{ color: accentColor, fontSize: '1.2rem' }} />
                                                <Typography sx={{
                                                    color: `${textColor}90`,
                                                    fontWeight: 500,
                                                    fontSize: { xs: '0.9rem', md: '1rem' }
                                                }}>
                                                    {feature}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Fade>
                            )}

                            {/* CTA Buttons */}
                            <Fade in={isVisible} timeout={1800}>
                                <Box sx={{
                                    display: 'flex',
                                    gap: { xs: 2, sm: 2 },
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    justifyContent: { xs: 'center', md: 'flex-start' },
                                    width: { xs: '100%', sm: 'auto' },
                                }}>
                                    <Tooltip
                                        title={isRTL() ? "ابدأ رحلتك معنا" : "Start Your Journey With Us"}
                                        arrow
                                        placement="top"
                                    >
                                        <Button
                                            variant="contained"
                                            size="large"
                                            href={ctaLink}
                                            {...(isRTL() ? { startIcon: <ArrowForward sx={{ transform: 'rotate(180deg)' }} /> } : { endIcon: <ArrowForward /> })}
                                            sx={{
                                                background: `linear-gradient(45deg, ${accentColor}, ${accentColor}CC)`,
                                                color: theme.palette.getContrastText(accentColor),
                                                px: { xs: 3, sm: 4 },
                                                py: { xs: 1.2, sm: 1.5 },
                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                                fontWeight: 600,
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                boxShadow: `0 8px 25px ${accentColor}40`,
                                                transition: 'all 0.3s ease',
                                                minHeight: { xs: '48px', sm: '56px' },
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    [getRightOrLeft()]: '-100%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: `linear-gradient(${isRTL() ? '270deg' : '90deg'}, transparent, rgba(255,255,255,0.3), transparent)`,
                                                    transition: `${getRightOrLeft()} 0.5s ease`
                                                },
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 12px 35px ${accentColor}60`,
                                                    '&::before': {
                                                        [getRightOrLeft()]: '100%'
                                                    }
                                                }
                                            }}
                                        >
                                            {ctaText}
                                        </Button>
                                    </Tooltip>

                                    {secondaryCtaText && (
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            href={secondaryCtaLink}
                                            sx={{
                                                borderColor: `${textColor}50`,
                                                color: textColor,
                                                px: { xs: 3, sm: 4 },
                                                py: { xs: 1.2, sm: 1.5 },
                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                                fontWeight: 600,
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                backdropFilter: 'blur(10px)',
                                                background: 'rgba(255,255,255,0.1)',
                                                transition: 'all 0.3s ease',
                                                minHeight: { xs: '48px', sm: '56px' },
                                                '&:hover': {
                                                    borderColor: accentColor,
                                                    color: accentColor,
                                                    background: 'rgba(255,255,255,0.2)',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            {secondaryCtaText}
                                        </Button>
                                    )}

                                    {showVideoButton && (
                                        <Tooltip
                                            title={isRTL() ? "شاهد الفيديو التعريفي" : "Watch Introduction Video"}
                                            arrow
                                            placement="top"
                                        >
                                            <IconButton
                                                size="large"
                                                href={videoUrl}
                                                sx={{
                                                    width: { xs: 56, sm: 64 },
                                                    height: { xs: 56, sm: 64 },
                                                    background: 'rgba(255,255,255,0.2)',
                                                    backdropFilter: 'blur(10px)',
                                                    color: textColor,
                                                    border: `2px solid ${accentColor}40`,
                                                    transition: 'all 0.3s ease',
                                                    position: 'relative',
                                                    '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '100%',
                                                        height: '100%',
                                                        borderRadius: '50%',
                                                        border: `2px solid ${accentColor}60`,
                                                        animation: 'ripple 2s infinite ease-out'
                                                    },
                                                    '&:hover': {
                                                        background: `${accentColor}80`,
                                                        transform: 'scale(1.1)',
                                                        borderColor: accentColor
                                                    }
                                                }}
                                            >
                                                <PlayArrow
                                                    sx={{
                                                        fontSize: '2rem',
                                                        transform: isRTL() ? 'scaleX(-1)' : 'none'
                                                    }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Fade>
                        </Box>
                    </Grid>

                    {/* Right Content */}
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            order: { xs: 1, md: 2 },
                            mb: { xs: 0, md: 0 }
                        }}
                    >
                        <Box sx={{
                            animation: isVisible ? 'slideInFromRight 1s ease-out' : 'none',
                            position: 'relative',
                            px: { xs: 1, sm: 2, md: 0 },
                        }}>
                            {/* Stats Cards */}
                            {showStats && (
                                <Grid
                                    container
                                    spacing={{ xs: 1, sm: 2 }}
                                    sx={{ mb: { xs: 3, md: 4 } }}
                                >
                                    {stats.map((stat: StatItem, idx: number) => (
                                        <Grid item xs={6} sm={6} key={idx}>
                                            <Zoom in={isVisible} timeout={1000 + idx * 200}>
                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        background: 'rgba(255,255,255,0.15)',
                                                        backdropFilter: 'blur(20px)',
                                                        border: `2px solid ${accentColor}22`,
                                                        borderRadius: 3,
                                                        textAlign: 'center',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-5px)',
                                                            border: `2px solid ${accentColor}`,
                                                            boxShadow: `0 10px 30px ${accentColor}30`
                                                        }
                                                    }}
                                                >
                                                    <CardContent sx={{
                                                        p: { xs: 1.5, sm: 2 },
                                                        '&:last-child': { pb: { xs: 1.5, sm: 2 } }
                                                    }}>
                                                        <Avatar
                                                            sx={{
                                                                background: `linear-gradient(45deg, ${accentColor}, ${accentColor}80)`,
                                                                color: theme.palette.getContrastText(accentColor),
                                                                width: { xs: 32, sm: 40 },
                                                                height: { xs: 32, sm: 40 },
                                                                mx: 'auto',
                                                                mb: 1
                                                            }}
                                                        >
                                                            {stat.icon}
                                                        </Avatar>
                                                        <Typography
                                                            variant="h5"
                                                            sx={{
                                                                color: textColor,
                                                                fontWeight: 700,
                                                                mb: 0.5,
                                                                fontSize: { xs: '1.2rem', sm: '1.5rem' }
                                                            }}
                                                        >
                                                            {stat.value}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: `${textColor}80`,
                                                                fontSize: { xs: '0.75rem', sm: '0.9rem' }
                                                            }}
                                                        >
                                                            {stat.label}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Zoom>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}

                            {/* Testimonial Card */}
                            {testimonial && (
                                <Fade in={isVisible} timeout={2000}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            background: 'rgba(255,255,255,0.1)',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            borderRadius: 3,
                                            mb: { xs: 2, md: 3 },
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-5px)'
                                            }
                                        }}
                                    >
                                        <CardContent sx={{
                                            p: { xs: 2, sm: 3 },
                                            '&:last-child': { pb: { xs: 2, sm: 3 } }
                                        }}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 2,
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                textAlign: { xs: 'center', sm: isRTL() ? 'right' : 'left' }
                                            }}>
                                                <Avatar
                                                    src={testimonial.avatar}
                                                    sx={{
                                                        width: { xs: 40, sm: 50 },
                                                        height: { xs: 40, sm: 50 },
                                                        [isRTL() ? 'ml' : 'mr']: { xs: 0, sm: 2 },
                                                        mb: { xs: 1, sm: 0 }
                                                    }}
                                                >
                                                    {testimonial.name?.charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography sx={{
                                                        color: textColor,
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                                    }}>
                                                        {testimonial.name}
                                                    </Typography>
                                                    <Typography sx={{
                                                        color: `${textColor}70`,
                                                        fontSize: { xs: '0.8rem', sm: '0.9rem' }
                                                    }}>
                                                        {testimonial.position}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Typography sx={{
                                                color: `${textColor}90`,
                                                fontStyle: 'italic',
                                                mb: 2,
                                                fontSize: { xs: '0.85rem', sm: '1rem' },
                                                lineHeight: 1.5,
                                                textAlign: isRTL() ? 'right' : 'left'
                                            }}>
                                                {isRTL() ? `"${testimonial.text}"` : `"${testimonial.text}"`}
                                            </Typography>
                                            <Rating
                                                value={testimonial.rating || 5}
                                                readOnly
                                                size={isExtraSmall ? "small" : "medium"}
                                                sx={{
                                                    justifyContent: { xs: 'center', sm: isRTL() ? 'flex-end' : 'flex-start' },
                                                    display: 'flex',
                                                    direction: 'ltr' // Rating stars always LTR
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Fade>
                            )}

                            {/* Contact Info */}
                            {contactInfo && (
                                <Fade in={isVisible} timeout={2200}>
                                    <Box sx={{
                                        background: 'rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: 3,
                                        p: { xs: 1.5, sm: 2 }
                                    }}>
                                        <Typography sx={{
                                            color: textColor,
                                            fontWeight: 600,
                                            mb: 2,
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            textAlign: { xs: 'center', sm: isRTL() ? 'right' : 'left' }
                                        }}>
                                            {isRTL() ? 'تواصل معنا الآن' : 'Contact Us Now'}
                                        </Typography>
                                        <Stack spacing={1}>
                                            {contactInfo.phone && (
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    justifyContent: { xs: 'center', sm: isRTL() ? 'flex-end' : 'flex-start' },
                                                    flexDirection: isRTL() ? 'row-reverse' : 'row'
                                                }}>
                                                    <Phone sx={{ color: accentColor, fontSize: '1.2rem' }} />
                                                    <Typography sx={{
                                                        color: `${textColor}90`,
                                                        fontSize: { xs: '0.85rem', sm: '1rem' },
                                                        direction: 'ltr' // Phone numbers always LTR
                                                    }}>
                                                        {contactInfo.phone}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {contactInfo.email && (
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    justifyContent: { xs: 'center', sm: isRTL() ? 'flex-end' : 'flex-start' },
                                                    flexDirection: isRTL() ? 'row-reverse' : 'row'
                                                }}>
                                                    <Email sx={{ color: accentColor, fontSize: '1.2rem' }} />
                                                    <Typography sx={{
                                                        color: `${textColor}90`,
                                                        fontSize: { xs: '0.85rem', sm: '1rem' },
                                                        direction: 'ltr' // Emails always LTR
                                                    }}>
                                                        {contactInfo.email}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {contactInfo.location && (
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    justifyContent: { xs: 'center', sm: isRTL() ? 'flex-end' : 'flex-start' },
                                                    flexDirection: isRTL() ? 'row-reverse' : 'row'
                                                }}>
                                                    <LocationOn sx={{ color: accentColor, fontSize: '1.2rem' }} />
                                                    <Typography sx={{
                                                        color: `${textColor}90`,
                                                        fontSize: { xs: '0.85rem', sm: '1rem' }
                                                    }}>
                                                        {contactInfo.location}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>
                                </Fade>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* Scroll Indicator */}
            {showScrollIndicator && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: { xs: 15, sm: 30 },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        animation: 'fadeInUp 2s ease-out, floating 3s ease-in-out infinite 2s',
                        cursor: 'pointer',
                        display: { xs: 'none', sm: 'block' }
                    }}
                    onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{
                            color: `${textColor}70`,
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            mb: 1
                        }}>
                            {isRTL() ? 'اكتشف المزيد' : 'Discover More'}
                        </Typography>
                        <KeyboardArrowDown
                            sx={{
                                color: accentColor,
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                                animation: 'floating 2s ease-in-out infinite'
                            }}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ServiceHero;
